---
title: 'Configuring your setup'
metaTitle: 'Admin-UI Docs | Getting started > Configuring your machine'
metaDescription: 'Explains what is needed for your machine to run Admin-UI project locally'
---

This documentation will guide you on making sure you have the right setup for your machine to run Admin-UI locally and be able to contribute to the repo.

> Note: If you have any problems during installation, please read the [Troubleshooting](/getting-started/09-troubleshooting) page.

## 1 - Install Git

To install [Git](https://git-scm.com/), please use the commands below for your corresponding operating system:

### Git on Linux

This command allows us to add the Git PPA repository to install the latest version of Git.
If we install the official ubuntu version, we'll have an out-of-date and corrupted version of Git.

```bash
sudo add-apt-repository ppa:git-core/ppa
sudo apt update && sudo apt upgrade -y git
```

### Git on OSX

```bash
brew install git
```

### Git on Windows

1. Go to https://git-scm.com/download/win
2. Download the installer and execute it by following the wizard

## 2 - Configuring your GitHub settings

### Access to the right workgroup

Make sure you have been added to the [Coveo Platform organization](https://github.com/coveo-platform) on GitHub, otherwise you must [send a request via our CoveoGO portal](https://www.coveoat.coveo.com/coveogo-forms) to the IT department.

If you are a new core admin-ui developer, you will need to be added as a member of the [developers team](https://github.com/orgs/coveo-platform/teams/developers).

### Authenticating with SSH to GitHub

If you don't have a key yet, we suggest using 1Password to [generate, store and use it](https://developer.1password.com/docs/ssh/get-started/#step-4-configure-your-ssh-or-git-client) (make sure to choose the type `Ed25519`). Using 1Password removes the need to manually copy files when switching devices, and the use of biometrics makes it more convenient to unlock the key, removing the need for a password on the key itself.

Since we use SSO for our GitHub organizations at Coveo, you will need to [authorize the key](https://docs.github.com/en/enterprise-cloud@latest/authentication/authenticating-with-saml-single-sign-on/authorizing-an-ssh-key-for-use-with-saml-single-sign-on) to be used on the ones you have access to.

### Signing commits (required at Coveo)

Follow the [set up commit signature documentation](https://coveord.atlassian.net/wiki/spaces/RD/pages/4038852714/Set+up+commit+signature) which also uses 1Password. You can use the same SSH key to authenticate and sign commits.

## 3 - Configure Git

```bash
git config --global user.name "your fullname"
git config --global user.email my@mail.coveo.com
```

The use of a `.gitignore` file at the global level is part of good development practice.
You will avoid adding rules to ignore that only match your development environment in the `.gitignore` files of projects versioned with Git.

Here is an example of a `.gitignore_global` file for the overall configuration of your development platform that you must change according to your needs.

```bash
###################
# Compiled source #
###################
*.com
*.class
*.dll
*.exe
*.o
*.so

############
# Packages #
############
# it's better to unpack these files and commit the raw source
# git has its own built in compression methods
*.7z
*.dmg
*.gz
*.iso
*.jar
*.rar
*.tar
*.zip

######################
# Logs and databases #
######################
*.log
*.sql
*.sqlite
get_dump.sh

######################
# OS generated files #
######################
.DS_Store
.DS_Store?
._*
.Spotlight-V100
.Trashes
ehthumbs.db
Thumbs.db
*~
.*.swp

#######
# IDE #
#######
nbproject
.idea
.project
.buildpath
.project
.code-workspace
.vscode/*
.setting/org.eclipse.php.core.prefs
.settings/org.eclipse.wst.common.project.facet.core.xml

##########
# DOCKER #
##########
docker-compose.override.yml
docker-compose.override.yaml

#########
# Other #
#########
issue.md
todo.md
```

### Configure Git on Linux & OSX

```bash
# configure your gitignore file for your global configuration (this file is used for to ignore all files of your development platform)
git config --global core.excludesfile "$HOME/.gitignore_global"
```

### Configure Git on Windows

```bash
# configure your gitignore file for your global configuration with PowerShell (this file is used for to ignore all files of your development platform)
git config --global core.excludesfile "$HOME\.gitignore_global"
```

## 4 - Enabling authentication to AWS CodeArtifact

The Admin UI repository refers some private packages hosted in AWS CodeArtifact, which uses our AWS SSO setup for authentication. We provide an NPM hook that will automatically add the right token to your `~/.npmrc` file before installing packages, but for it to work you need to have a valid SSO session.

Please read [this documentation](https://coveord.atlassian.net/wiki/spaces/SFINT/pages/4887642131/Setup+AWS+credentials) explaining how to setup your SSO login. Once you are able to run `aws sso login` successfully, you should be good to go.

## 5 - Install NVM

[Node Version Manager](https://github.com/nvm-sh/nvm) (or NVM) makes it easier to install and update Node.js, we strongly advise to manage Node.js version this way instead of installing it manually.

Please uninstall any existing versions of NVM, Node.js and NPM before installing NVM.

### NVM on Linux

The command which follows is currently used for the installation but [we advise you to visit the creator's repository for more complete documentation](https://github.com/nvm-sh/nvm#installing-and-updating)

```bash
# go to https://github.com/nvm-sh/nvm/releases to get the latest version number (in my case it was 0.40.1)
curl -o- https://raw.githubusercontent.com/nvm-sh/nvm/v0.40.1/install.sh | bash
```

### NVM on OSX

For the installation of NVM on OSX we offer 2 choices:

1. [installation as on Linux with the installer from the creator's repository](https://nodesource.com/blog/installing-node-js-tutorial-using-nvm-on-mac-os-x-and-ubuntu/)
2. [installation via homebrew which should be easier](https://tecadmin.net/install-nvm-macos-with-homebrew/)

### NVM on Windows

For the installation of NVM on Windows you can follow [the installation described on the nvm-windows project repository](https://github.com/coreybutler/nvm-windows#installation--upgrades).

## 6 - Install Node.js with NVM

To install [Node.js](https://nodejs.org/en/), please open a terminal (use PowerShell or cmd with Windows)

```bash
# install the current LTS version of Node.js
nvm install --lts --default
nvm install-latest-npm

# verify the installation
node --version
npm --version
```

## 7 - Install PNPM

To install [PNPM](https://pnpm.io/installation), please open a terminal (use PowerShell or cmd with Windows)

```bash
# install the latest version of pnpm with npm
npm install --global pnpm

# or install the latest version of pnpm with npx
npx pnpm add --global pnpm

# verify the installation
pnpm --version

# make sure it's set up to use the package-lock file
pnpm config set package-lock true
```

To change your pnpm version, run `pnpm update --global pnpm` in your terminal.

## 8 - Install mkcert

[mkcert](https://github.com/FiloSottile/mkcert) is a small utility used to create local self-signed certificates. We use this tool to launch the local development server using HTTPS.

1. Follow [the installation instructions on the official repository README](https://github.com/FiloSottile/mkcert?tab=readme-ov-file#installation) according to your operating system.

2. Once `mkcert` is installed on your machine, create a local certificate authority (CA) and install it in the system store. This will allow your browser to trust your own local certificates. This is automatically done by running the following command. You only need to run it once. 
    ```bash
    mkcert -install
    ```
3. Restart your browser. Now that the local CA has been added to your system store, the browser needs to be restared.

## 9 - Update the host file (optional)

- `localhost.corp.coveo.com` useful to share your local demos with your peers over the coveo local network (optional).

### on Linux & OSX

Edit `/etc/hosts` with admin rights and add this line.

```bash
127.0.0.1 localhost.corp.coveo.com
```

### on Windows

Edit `C:\Windows\System32\drivers\etc\hosts` with admin rights and add this line.

```bash
127.0.0.1 localhost.corp.coveo.com
```
